import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Edit2, ArrowRight } from 'lucide-react';
import { CardSectionProps } from '../../../types/compositeCards';
import { PrimeArtifact, PrimeArtifactStateTransition } from '../../../types/primeModel';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import styles from './PrimeTransitionsContextCard.module.css';

interface PrimeTransitionsContextCardProps extends CardSectionProps {}

export const PrimeTransitionsContextCard: React.FC<PrimeTransitionsContextCardProps> = ({ card, onAction }) => {
  const [transitions, setTransitions] = useState<PrimeArtifactStateTransition[]>([]);
  const [states, setStates] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  const data = card.data as any;
  const primeArtifact: PrimeArtifact = data.primeArtifact || data.artifact;
  const isArchitect = card.context.uiRole === 'architect';

  useEffect(() => {
    const loadTransitions = async () => {
      if (!primeArtifact?.id) return;
      
      setLoading(true);
      try {
        const artifact = await PrimeArtifactService.getById(primeArtifact.id);
        setTransitions(artifact.transitions || []);
        setIsApproved(artifact.transitions_approved || false);
        
        // Create state name mapping
        const stateMap: Record<string, string> = {};
        (artifact.states || []).forEach(state => {
          stateMap[state.id] = state.name;
        });
        setStates(stateMap);
      } catch (error) {
        console.error('Failed to load prime transitions:', error);
      } finally {
        setLoading(false);
      }
    };

    void loadTransitions();
  }, [primeArtifact?.id]);

  const handleToggleApproval = async () => {
    if (!primeArtifact?.id) return;
    
    try {
      if (isApproved) {
        await PrimeArtifactService.rejectTransitions(primeArtifact.id);
        setIsApproved(false);
      } else {
        await PrimeArtifactService.approveTransitions(primeArtifact.id);
        setIsApproved(true);
      }
    } catch (error) {
      console.error('Failed to toggle transitions approval:', error);
    }
  };

  if (loading) {
    return <div className={styles.primeTransitionsContextLoading}>Loading transitions...</div>;
  }

  return (
    <div className={styles.primeTransitionsContextCard}>
      <div className={styles.primeTransitionsHeader}>
        <h3>Prime Transitions</h3>
        {isArchitect && (
          <button
            className={`${styles.approvalToggle} ${isApproved ? styles.approved : styles.notApproved}`}
            onClick={handleToggleApproval}
          >
            {isApproved ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {isApproved ? 'Approved' : 'Not Approved'}
          </button>
        )}
      </div>

      {transitions.length === 0 ? (
        <div className={styles.noTransitionsMessage}>
          <p>No transitions defined yet</p>
        </div>
      ) : (
        <div className={styles.transitionsList}>
          {transitions.map((transition) => (
            <div key={transition.id} className={styles.transitionItem}>
              <div className={styles.transitionFlow}>
                <span className={styles.fromState}>{states[transition.from_state_ref] || 'Unknown'}</span>
                <ArrowRight size={16} className={styles.arrowIcon} />
                <span className={styles.toState}>{states[transition.to_state_ref] || 'Unknown'}</span>
              </div>
              <div className={styles.transitionName}>{transition.name}</div>
              {transition.description && (
                <div className={styles.transitionDescription}>{transition.description}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {isArchitect && (
        <div className={styles.primeTransitionsFooter}>
          <button 
            className={styles.editTransitionsButton}
            onClick={() => onAction?.('edit-transitions', { primeArtifact })}
          >
            <Edit2 size={14} />
            Edit Transitions
          </button>
        </div>
      )}
    </div>
  );
};

